/**
 * UnifiedAnchorSymbolLayer Component
 *
 * Owns the unified `map-cluster-source` and renders anchor symbols for all
 * three artifact kinds in a single feature collection: task markers, note
 * pins, and obstruction anchors. One feature per task at
 * `firstMarker.calculatedCenter ?? task.anchorPoint`; tasks without a Marker
 * geometry inflate the cluster count without a glyph (their geometries draw
 * via `TaskGeometryOverlays`). Notes and obstructions contribute their own
 * anchor features alongside.
 *
 * Layers (bottom → top): cluster circles + count, task border + composite,
 * obstruction halo, obstruction symbol, note pin + icon. The tier order
 * encodes cursor priority — `ALL_LAYER_IDS` below is the source of truth.
 * Click/drag interactions live in `useTaskLayerInteractions` /
 * `useTaskLayerDrag` (tasks), `useObstructionLayerInteractions`
 * (obstructions), and `NoteSymbolLayer` (notes), each scoped per layer ID.
 * The note / obstruction click handlers share the `useMapLayerClick`
 * primitive in `components/map/hooks`.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/UnifiedAnchorSymbolLayer
 */

import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import type { ExpressionSpecification } from '@maplibre/maplibre-gl-style-spec';
import { useMantineColorScheme } from '@mantine/core';

import type { FeatureCollection, Point } from 'geojson';

import { useGeoMarkupStore } from '@/store';
import {
  useTaskGeoJson,
  type TaskFeatureProperties,
} from './hooks/useTaskGeoJson';
import {
  useObstructionAnchorGeoJson,
  type ObstructionFeatureProperties,
} from './hooks/useObstructionAnchorGeoJson';
import { useTaskLayerInteractions } from './hooks/useTaskLayerInteractions';
import { useObstructionLayerInteractions } from './hooks/useObstructionLayerInteractions';
import { useTaskLayerDrag } from './hooks/useTaskLayerDrag';
import { getSpriteCache, registerSpritesOnMap } from '../symbolSprites';
import {
  LAYER_IDS,
  BORDER_COLOR_LIGHT,
  BORDER_COLOR_DARK,
  CLUSTER_COLOR_LIGHT,
  CLUSTER_COLOR_DARK,
  markerBorderLayer,
  markerCompositeLayer,
  clusterCirclesLayer,
  clusterCountLayer,
  clusterProperties,
  CLUSTER_RADIUS,
  CLUSTER_MAX_ZOOM,
} from './styles/symbolLayerStyles';
import {
  OBSTRUCTION_LAYER_IDS,
  obstructionHaloLayer,
  obstructionSymbolLayer,
} from './styles/obstructionSymbolLayerStyles';
import { MAP_CLUSTER_SOURCE_ID } from '../MapClusterLayer/featureKind';
import {
  NOTE_LAYER_IDS,
  noteMarkerPinLayer,
  noteMarkerIconLayer,
  useNoteGeoJson,
  type NoteFeatureProperties,
  type RenderableNote,
} from '../NoteSymbolLayer';
import type { Task } from '@/types';

// ===== Types =====

interface UnifiedAnchorSymbolLayerProps {
  tasks: Task[];
  /** Notes to render alongside tasks in the unified cluster source.
   *  Optional — `TaskMiniMap` / `MapOverlay` previews omit it. */
  notes?: RenderableNote[];
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
}

const EMPTY_NOTES: RenderableNote[] = [];

type ClusterFeatureProperties =
  | TaskFeatureProperties
  | NoteFeatureProperties
  | ObstructionFeatureProperties;

// ===== Layer Helpers =====

/** Bottom→top: clusters → task markers → obstruction halo+icon → note pins.
 *  Obstruction halo renders below the task marker tiers so a co-located task
 *  marker still dominates; the obstruction icon sits above task markers but
 *  below the note pin so the active note (drawn with the selected scale)
 *  always wins for cursor priority. */
const ALL_LAYER_IDS = [
  LAYER_IDS.CLUSTER_CIRCLES,
  LAYER_IDS.CLUSTER_COUNT,
  LAYER_IDS.MARKER_BORDER,
  LAYER_IDS.MARKER_COMPOSITE,
  OBSTRUCTION_LAYER_IDS.HALO,
  OBSTRUCTION_LAYER_IDS.SYMBOL,
  NOTE_LAYER_IDS.MARKER_PIN,
  NOTE_LAYER_IDS.MARKER_ICON,
] as const;

type LayerSpec = Parameters<MaplibreMap['addLayer']>[0];

function addLayerIfMissing(map: MaplibreMap, spec: LayerSpec): void {
  if (!map.getLayer(spec.id)) map.addLayer(spec);
}

interface ThemeColors {
  border: string;
  clusterFill: ExpressionSpecification;
  clusterStroke: string;
  clusterText: string;
}

function addLayers(map: MaplibreMap, theme: ThemeColors): void {
  addLayerIfMissing(map, {
    ...clusterCirclesLayer,
    source: MAP_CLUSTER_SOURCE_ID,
    paint: {
      ...clusterCirclesLayer.paint,
      'circle-color': theme.clusterFill,
      'circle-stroke-color': theme.clusterStroke,
    },
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...clusterCountLayer,
    source: MAP_CLUSTER_SOURCE_ID,
    paint: {
      ...clusterCountLayer.paint,
      'text-color': theme.clusterText,
    },
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...markerBorderLayer,
    source: MAP_CLUSTER_SOURCE_ID,
    paint: { ...markerBorderLayer.paint, 'icon-color': theme.border },
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...markerCompositeLayer,
    source: MAP_CLUSTER_SOURCE_ID,
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...obstructionHaloLayer,
    source: MAP_CLUSTER_SOURCE_ID,
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...obstructionSymbolLayer,
    source: MAP_CLUSTER_SOURCE_ID,
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...noteMarkerPinLayer,
    source: MAP_CLUSTER_SOURCE_ID,
  } as LayerSpec);

  addLayerIfMissing(map, {
    ...noteMarkerIconLayer,
    source: MAP_CLUSTER_SOURCE_ID,
  } as LayerSpec);
}

/** Move marker layers to top — only when not already topmost */
function ensureLayersOnTop(map: MaplibreMap): void {
  const styleLayers = map.getStyle().layers;
  if (!styleLayers) return;

  const topN = styleLayers.slice(-ALL_LAYER_IDS.length);
  const alreadyOnTop =
    topN.length === ALL_LAYER_IDS.length &&
    topN.every((layer, index) => layer.id === ALL_LAYER_IDS[index]);
  if (alreadyOnTop) return;

  for (const layerId of ALL_LAYER_IDS) {
    if (map.getLayer(layerId)) map.moveLayer(layerId);
  }
}

function removeLayers(map: MaplibreMap): void {
  // MapLibre may have torn down the style during async teardown (StrictMode
  // unmount, route change, terrain toggle). Touching getLayer/getSource on a
  // dead style throws inside maplibre internals — bail out cleanly.
  if (!map.getStyle()) return;
  for (const layerId of ALL_LAYER_IDS) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  if (map.getSource(MAP_CLUSTER_SOURCE_ID))
    map.removeSource(MAP_CLUSTER_SOURCE_ID);
}

// ===== Component =====

export const UnifiedAnchorSymbolLayer = memo(function UnifiedAnchorSymbolLayer({
  tasks,
  notes = EMPTY_NOTES,
  mapRef,
  isMapReady,
}: UnifiedAnchorSymbolLayerProps) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const themeColors = useMemo<ThemeColors>(
    () => ({
      border: isDark ? BORDER_COLOR_DARK : BORDER_COLOR_LIGHT,
      clusterFill: isDark ? CLUSTER_COLOR_DARK : CLUSTER_COLOR_LIGHT,
      clusterStroke: isDark ? BORDER_COLOR_DARK : BORDER_COLOR_LIGHT,
      clusterText: isDark ? BORDER_COLOR_DARK : BORDER_COLOR_LIGHT,
    }),
    [isDark]
  );
  const themeRef = useRef(themeColors);
  themeRef.current = themeColors;

  const editingTaskId = useGeoMarkupStore((state) =>
    state.tasks.formIsOpen ? state.tasks.selectedId : null
  );
  const selectedNoteId = useGeoMarkupStore((state) => state.notes.selectedId);

  const taskGeojson = useTaskGeoJson({ tasks, editingTaskId });
  const noteGeojson = useNoteGeoJson({ notes, selectedId: selectedNoteId });
  const obstructionGeojson = useObstructionAnchorGeoJson();

  const geojson = useMemo<FeatureCollection<Point, ClusterFeatureProperties>>(
    () => ({
      type: 'FeatureCollection',
      features: [
        ...taskGeojson.features,
        ...noteGeojson.features,
        ...obstructionGeojson.features,
      ],
    }),
    [taskGeojson, noteGeojson, obstructionGeojson]
  );

  // useState (not useRef) so that flipping it triggers a re-render and the
  // data-sync effect below re-fires with the latest geojson — covers the case
  // where setup() completes asynchronously after `geojson` already stabilised
  // (e.g. Dashboard → Map navigation where mapTasks is preserved in the store
  // and the post-fetch tasksSignature matches the cached one, so the geojson
  // memo never produces a new reference).
  const [sourceReady, setSourceReady] = useState(false);
  const geojsonRef = useRef(geojson);
  geojsonRef.current = geojson;

  // Register sprites → add source → add layers (all imperative)
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    let cancelled = false;

    const setup = async (): Promise<void> => {
      // 1. Get shared sprite cache (also used by NoteSymbolLayer)
      const sprites = await getSpriteCache();
      if (cancelled) return;

      // 2. Register sprites on map
      registerSpritesOnMap(map, sprites);

      // 3. Add or refresh GeoJSON source. If a previous cleanup failed (e.g.
      // StrictMode unmount race against MapLibre style teardown), the source
      // can survive into the next mount holding stale data. Reuse it but push
      // the latest features so the map shows the current dataset.
      const existingSource = map.getSource(MAP_CLUSTER_SOURCE_ID);
      if (!existingSource) {
        map.addSource(MAP_CLUSTER_SOURCE_ID, {
          type: 'geojson',
          data: geojsonRef.current,
          cluster: true,
          clusterRadius: CLUSTER_RADIUS,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
          clusterProperties,
        });
      } else if ('setData' in existingSource) {
        (existingSource as GeoJSONSource).setData(geojsonRef.current);
      }

      // 4. Add symbol layers
      addLayers(map, themeRef.current);
      setSourceReady(true);

      // 5. Move to top after a tick (other layers may add during same frame)
      requestAnimationFrame(() => {
        if (!cancelled) ensureLayersOnTop(map);
      });
    };

    // Single style.load handler: re-runs setup on every style reload (terrain
    // toggle, basemap swap). When the style is already loaded at mount time,
    // we kick setup off ourselves; otherwise the listener will catch the
    // initial load. Using one handler avoids the previous double-setup race
    // (`once` + `on` both firing on the first style.load).
    const handleStyleLoad = (): void => {
      setSourceReady(false);
      setup();
    };
    map.on('style.load', handleStyleLoad);

    if (map.isStyleLoaded()) setup();

    const handleIdle = (): void => {
      // Backstop: if `style.load` was missed (e.g. fired in the race window
      // between StrictMode cleanup and re-mount on Dashboard → Map navigation),
      // the source was never created. `idle` fires after the style settles,
      // so isStyleLoaded() is true here — kick setup once. The source guard
      // keeps subsequent `idle` events (after every pan/zoom) cheap.
      if (
        !cancelled &&
        !map.getSource(MAP_CLUSTER_SOURCE_ID) &&
        map.isStyleLoaded()
      ) {
        setup();
        return;
      }
      if (map.getLayer(LAYER_IDS.MARKER_COMPOSITE)) ensureLayersOnTop(map);
    };
    map.on('idle', handleIdle);

    return () => {
      cancelled = true;
      map.off('style.load', handleStyleLoad);
      map.off('idle', handleIdle);
      try {
        removeLayers(map);
      } catch (error) {
        // Map style may already be torn down (StrictMode unmount, route change).
        if (import.meta.env.DEV) {
          console.warn('UnifiedAnchorSymbolLayer: removeLayers failed', error);
        }
      }
      setSourceReady(false);
    };
  }, [mapRef, isMapReady]);

  // Sync theme-aware colors on all layers
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    if (map.getLayer(LAYER_IDS.MARKER_BORDER)) {
      map.setPaintProperty(
        LAYER_IDS.MARKER_BORDER,
        'icon-color',
        themeColors.border
      );
    }
    if (map.getLayer(LAYER_IDS.CLUSTER_CIRCLES)) {
      map.setPaintProperty(
        LAYER_IDS.CLUSTER_CIRCLES,
        'circle-color',
        themeColors.clusterFill
      );
      map.setPaintProperty(
        LAYER_IDS.CLUSTER_CIRCLES,
        'circle-stroke-color',
        themeColors.clusterStroke
      );
    }
    if (map.getLayer(LAYER_IDS.CLUSTER_COUNT)) {
      map.setPaintProperty(
        LAYER_IDS.CLUSTER_COUNT,
        'text-color',
        themeColors.clusterText
      );
    }
  }, [themeColors, mapRef, isMapReady]);

  // Keeps the source data in sync. Re-fires whenever `geojson` changes (data
  // fetch resolved, edited task toggled) AND whenever `sourceReady` flips to
  // true — the latter catches the case where `geojson` stabilised before
  // setup() finished its async sprite-cache step (route remount with cached
  // mapTasks), so the very first sync pass picks the latest data.
  useEffect(() => {
    if (!sourceReady) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const source = map.getSource(MAP_CLUSTER_SOURCE_ID) as
      | GeoJSONSource
      | undefined;
    if (source) {
      source.setData(geojson);
    }
  }, [geojson, sourceReady, mapRef]);

  // Note clicks are wired in NoteSymbolLayer (separate component) so the two
  // hit-testing pipelines stay independent.
  useTaskLayerInteractions({ mapRef, isMapReady });
  useTaskLayerDrag({ mapRef, isMapReady, geojson: taskGeojson, tasks });
  // Obstruction click handler — also kept local because the hit-test layer ID
  // is owned by this component (single cluster source, one symbol layer per
  // artifact kind).
  useObstructionLayerInteractions({ mapRef, isMapReady });

  return null;
});
